"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports.Box = void 0;
exports.Scales = Scales;
exports.scaleDistance = scaleDistance;
var tslib_1 = require("tslib");
var lodash_1 = require("lodash");
var d3 = tslib_1.__importStar(require("./d3-modules"));
exports.Box = {
  /** Return a newly created `Box` */
  create: function (xmin, ymin, xmax, ymax) {
    return {
      xmin: xmin,
      ymin: ymin,
      xmax: xmax,
      ymax: ymax
    };
  },
  /** Return width of a `Box` */
  width: function (box) {
    return box.xmax - box.xmin;
  },
  /** Return height of a `Box` */
  height: function (box) {
    return box.ymax - box.ymin;
  },
  /** Decide whether a 2D point is within a `Box` (including its boundary) */
  containsPoint: function (box, point) {
    return point.x >= box.xmin && point.x <= box.xmax && point.y >= box.ymin && point.y <= box.ymax;
  },
  /** Return the portion of `box` which lies within `constraint` box.
   * If `minSize` is provided, expand the resulting box to ensure minimum box size (unless it completely fills the `constraint` box). */
  clamp: function (box, constraint, minSize) {
    var clamped = {
      xmin: isNaN(box.xmin) ? constraint.xmin : (0, lodash_1.clamp)(box.xmin, constraint.xmin, constraint.xmax),
      xmax: isNaN(box.xmax) ? constraint.xmax : (0, lodash_1.clamp)(box.xmax, constraint.xmin, constraint.xmax),
      ymin: isNaN(box.ymin) ? constraint.ymin : (0, lodash_1.clamp)(box.ymin, constraint.ymin, constraint.ymax),
      ymax: isNaN(box.ymax) ? constraint.ymax : (0, lodash_1.clamp)(box.ymax, constraint.ymin, constraint.ymax)
    };
    if (minSize === undefined) {
      return clamped;
    } else {
      return expandBox(clamped, constraint, minSize);
    }
  },
  /** Snap box boundaries to integer values.
   * If snapStrategy==='out', move boundaries outwards;
   * if snapStrategy==='nearest', round boundaries to nearest integers.
   * If `constraints` is provided, clamp resulting box to `constraints`.
   * Return `undefined` if resulting box collapses to zero-width or zero-height. */
  snap: function (box, snapStrategy, constraints) {
    if (!box) return undefined;
    var snapped = snapStrategy === 'out' ? {
      // Snap out
      xmin: Math.floor(box.xmin),
      xmax: Math.ceil(box.xmax),
      ymin: Math.floor(box.ymin),
      ymax: Math.ceil(box.ymax)
    } : {
      // Snap to nearest boundary
      xmin: Math.round(box.xmin),
      xmax: Math.round(box.xmax),
      ymin: Math.round(box.ymin),
      ymax: Math.round(box.ymax)
    };
    if (constraints) {
      snapped = exports.Box.clamp(snapped, constraints);
    }
    if (snapped.xmin === snapped.xmax || snapped.ymin === snapped.ymax) {
      // Empty box
      return undefined;
    }
    return snapped;
  }
};
/** Expand `interval` to ensure minimum width, but do not expand out of the `constraint` interval.
 * (Resulting interval will be smaller that `minWidth` if it completely fills the `constraint` interval.) */
function expandInterval(interval, constraint, minWidth) {
  if (minWidth === void 0) {
    minWidth = 0;
  }
  var min = interval[0],
    max = interval[1];
  var constraintMin = constraint[0],
    constraintMax = constraint[1];
  if (max - min >= minWidth) {
    // Already wide enough
    return [min, max];
  }
  var center = 0.5 * (min + max);
  if (center < constraintMin + 0.5 * minWidth) {
    // Will touch constraintMin
    return [constraintMin, Math.min(constraintMin + minWidth, constraintMax)];
  }
  if (center > constraintMax - 0.5 * minWidth) {
    // Will touch constraintMax
    return [Math.max(constraintMax - minWidth, constraintMin), constraintMax];
  }
  return [center - 0.5 * minWidth, center + 0.5 * minWidth];
}
/** Expand `box` to ensure minimum box size, but do not expand out of the `constraint` box.
 * (Resulting box will be smaller that `minSize` if it completely fills the `constraint` box in either direction.) */
function expandBox(box, constraint, minSize) {
  var _a = expandInterval([box.xmin, box.xmax], [constraint.xmin, constraint.xmax], minSize.width),
    xmin = _a[0],
    xmax = _a[1];
  var _b = expandInterval([box.ymin, box.ymax], [constraint.ymin, constraint.ymax], minSize.height),
    ymin = _b[0],
    ymax = _b[1];
  return {
    xmin: xmin,
    xmax: xmax,
    ymin: ymin,
    ymax: ymax
  };
}
function getXScale(source, dest) {
  return d3.scaleLinear([source.xmin, source.xmax], [dest.xmin, dest.xmax]).clamp(false);
}
function getYScale(source, dest) {
  return d3.scaleLinear([source.ymin, source.ymax], [dest.ymin, dest.ymax]).clamp(false);
}
function Scales(boxes) {
  return {
    worldToCanvas: {
      x: getXScale(boxes.visWorld, boxes.canvas),
      y: getYScale(boxes.visWorld, boxes.canvas)
    },
    canvasToWorld: {
      x: getXScale(boxes.canvas, boxes.visWorld),
      y: getYScale(boxes.canvas, boxes.visWorld)
    }
  };
}
/** Apply scale to a distance of two points */
function scaleDistance(scale, distance) {
  if (scale.clamp()) throw new Error('NotImplementedError: this function is not implemented for clamping scales');
  return scale(distance) - scale(0);
}